import { motion } from 'framer-motion';
import { MetaTags } from '../../components/seo/MetaTags';
import { Shield } from 'lucide-react';

export function Privacy() {
  return (
    <>
      <MetaTags
        title="Privacy Policy"
        description="Learn how Plant Identifier collects, uses, and protects your personal information."
      />
      <div className="min-h-screen bg-gradient-to-b from-emerald-50/50 to-transparent pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto px-4"
        >
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center p-2 bg-emerald-100 rounded-full mb-4">
              <Shield className="w-6 h-6 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-gray-600">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                1. Introduction
              </h2>
              <p className="text-gray-600">
                This Privacy Policy explains how Plant Identifier collects,
                uses, and protects your personal information. We are committed
                to protecting your privacy and handling your data transparently.
              </p>
            </section>

            {/* Information Collection */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                2. Information We Collect
              </h2>
              <div className="space-y-4 text-gray-600">
                <h3 className="font-medium text-gray-800">
                  2.1 Personal Information
                </h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>Name and email address</li>
                  <li>Payment information (processed by Razorpay)</li>
                  <li>Plant images you upload</li>
                  <li>Usage data and identification history</li>
                </ul>

                <h3 className="font-medium text-gray-800">
                  2.2 Payment Information
                </h3>
                <p>
                  Payment processing is handled by Razorpay. We do not store
                  your complete payment information. Razorpay maintains the
                  security of your payment data in accordance with PCI-DSS
                  standards.
                </p>
              </div>
            </section>

            {/* Use of Information */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                3. How We Use Your Information
              </h2>
              <div className="space-y-4 text-gray-600">
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    To provide and improve our plant identification service
                  </li>
                  <li>To process payments and manage your account</li>
                  <li>To communicate service updates and promotional offers</li>
                  <li>To analyze service usage and improve user experience</li>
                  <li>To comply with legal obligations</li>
                </ul>
              </div>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                4. Data Security
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  We implement appropriate security measures to protect your
                  personal information:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security assessments</li>
                  <li>Access controls and authentication</li>
                  <li>Secure payment processing through Razorpay</li>
                </ul>
              </div>
            </section>

            {/* Payment Data */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                5. Payment Data Handling
              </h2>
              <div className="space-y-4 text-gray-600">
                <h3 className="font-medium text-gray-800">
                  5.1 Payment Processing
                </h3>
                <p>
                  Payments are processed securely by Razorpay. When you make a
                  payment:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    Your payment data is encrypted using industry standards
                  </li>
                  <li>
                    We only store the last 4 digits of your card for reference
                  </li>
                  <li>
                    Transaction history is maintained for legal compliance
                  </li>
                  <li>
                    Payment disputes are handled through Razorpay's system
                  </li>
                </ul>

                <h3 className="font-medium text-gray-800">
                  5.2 Transaction Records
                </h3>
                <p>
                  We maintain records of your credit purchases and usage for
                  accounting purposes and to resolve any disputes.
                </p>
              </div>
            </section>

            {/* Data Sharing */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                6. Information Sharing
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>We share your information with:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Razorpay for payment processing</li>
                  <li>Service providers who assist in our operations</li>
                  <li>Law enforcement when required by law</li>
                </ul>
              </div>
            </section>

            {/* User Rights */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                7. Your Rights
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>You have the right to:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Access your personal information</li>
                  <li>Correct inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Export your data</li>
                  <li>Opt-out of marketing communications</li>
                </ul>
              </div>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                8. Contact Us
              </h2>
              <p className="text-gray-600">
                For privacy-related questions or to exercise your rights,
                contact us at: privacy@plantidentifier.com
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </>
  );
}
